import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import SortableGrid from "../components/SortableGrid";
import api from "../lib/api";
import { fmtCur, fmtDate, fmtNum } from "../lib/format";
import { useCurrencyMode } from "../lib/useCurrencyMode";
import { toast } from "sonner";
import { DownloadSimple } from "@phosphor-icons/react";

const norm = (s) => String(s || "").toUpperCase().replace(/[^A-Z0-9]/g, "");

// Cache last fetched list + scroll position so returning here (e.g. browser Back after
// opening a dispatch) restores the exact list and scroll instead of re-fetching and
// jumping to the top. Module-level so it survives unmount. Mirrors the Orders grid.
const dispListCache = { key: null, data: null, scrollY: 0 };

export default function MsDispatch() {
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const [invs, setInvs] = useState([]);
  const [plants, setPlants] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [tpMap, setTpMap] = useState({});
  const [loading, setLoading] = useState(false);
  const mode = useCurrencyMode();

  const plant = params.get("plant") || "";
  const customer = params.get("customer") || "";
  const q = params.get("q") || "";

  useEffect(() => {
    api.get("/ms/plants").then((r) => setPlants(r.data)).catch(() => {});
    api.get("/ms/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/dispatch/transfer-parties").then((r) => {
      const m = {}; (r.data || []).forEach((t) => {
        [t.canon_pcode, t.pcode, t.party].forEach((k) => { if (k) m[norm(k)] = t; });
      });
      setTpMap(m);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const qs = new URLSearchParams();
    if (plant) qs.set("plant", plant);
    if (customer) qs.set("customer", customer);
    if (q) qs.set("q", q);
    if (mode) qs.set("mode", mode);
    const key = qs.toString();

    // Same query as last time (e.g. back button) → hydrate from cache + restore scroll.
    if (dispListCache.key === key && dispListCache.data) {
      setInvs(dispListCache.data);
      setLoading(false);
      const y = dispListCache.scrollY || 0;
      if (y > 0) {
        let tries = 0;
        const restore = () => {
          window.scrollTo(0, y);
          if (Math.abs(window.scrollY - y) > 2 && tries++ < 40) setTimeout(restore, 40);
        };
        requestAnimationFrame(restore);
      }
      return;
    }
    setLoading(true);
    api.get(`/ms/invoices?${key}`)
      .then((r) => { setInvs(r.data); dispListCache.key = key; dispListCache.data = r.data; dispListCache.scrollY = 0; })
      .finally(() => setLoading(false));
  }, [plant, customer, q, mode]);

  // Remember scroll position when leaving so back-navigation lands where we left.
  useEffect(() => {
    const save = () => { dispListCache.scrollY = window.scrollY; };
    window.addEventListener("scroll", save, { passive: true });
    return () => { save(); window.removeEventListener("scroll", save); };
  }, []);

  const [dl, setDl] = useState(false);
  const downloadDateFixReport = async () => {
    setDl(true);
    try {
      const res = await api.get("/import/date-fix-report.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice_date_fixes_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success("Date-fix report downloaded");
    } catch (e) { toast.error("Could not generate the date-fix report"); }
    finally { setDl(false); }
  };
  const [dlr, setDlr] = useState(false);
  const downloadReconcile = async () => {
    setDlr(true);
    try {
      const res = await api.get("/import/excel-vs-mssql-reconcile.xlsx?since=2025-01-01", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `excel_vs_mssql_reconcile_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success("Reconciliation report downloaded");
    } catch (e) { toast.error("Could not generate the reconciliation report"); }
    finally { setDlr(false); }
  };
  const [dlo, setDlo] = useState(false);
  const [dlpt, setDlpt] = useState(false);
  const downloadPoTransferReconcile = async () => {
    setDlpt(true);
    try {
      const res = await api.get("/import/po-transfer-reconcile-report.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `po_transfer_reconcile_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success("PO transfer reconciliation report downloaded");
    } catch (e) { toast.error("Could not generate the PO transfer reconciliation report"); }
    finally { setDlpt(false); }
  };
  const downloadOpenPoReconcile = async () => {
    setDlo(true);
    try {
      const res = await api.get("/import/open-po-reconcile-report.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `open_po_reconcile_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success("Open-PO reconciliation report downloaded");
    } catch (e) { toast.error("Could not generate the open-PO reconciliation report"); }
    finally { setDlo(false); }
  };

  const setParam = (k, v) => {
    const np = new URLSearchParams(params);
    if (v) np.set(k, v); else np.delete(k);
    setParams(np);
  };

  const dsp = (i) => i.display || {};
  const columns = [
    { key: "ms_sale_no", header: "Sale #", render: (i) => <strong className="text-mono">{i.ms_sale_no}</strong> },
    { key: "ms_inv_no", header: "Invoice #", render: (i) => <span className="text-mono">{i.ms_inv_no || "—"}</span> },
    { key: "comp_code", header: "Plant", render: (i) => <span className="jde-badge confirmed">{i.comp_code}</span> },
    { key: "sale_date", header: "Sale Date", type: "date", accessor: (i) => i.sale_date,
      render: (i) => <span className="text-mono" style={{ fontSize: 11 }}>{fmtDate(i.sale_date)}</span> },
    { key: "customer_name", header: "Customer",
      render: (i) => { const tp = tpMap[norm(i.ms_pcode)] || tpMap[norm(i.customer_name)]; return (
        <span>{i.customer_name}
          {tp && <span className="jde-badge" data-testid={`disp-tp-${i.ms_sale_no}`} style={{ marginLeft: 6, fontSize: 8, background: "#0F766E", color: "white", fontWeight: 700 }} title={`Transfer party: ${tp.tp_name || tp.tp_code}`}>TP ↳ {tp.tp_name || tp.tp_code}</span>}
        </span>); } },
    { key: "ms_pono", header: "PO #", render: (i) => <span className="text-mono">{i.ms_pono || "—"}</span> },
    { key: "veh_no", header: "Vehicle", accessor: (i) => i.dispatch?.veh_no,
      render: (i) => <span className="text-mono">{i.dispatch?.veh_no || "—"}</span> },
    { key: "gr_no", header: "GR #", accessor: (i) => i.dispatch?.gr_no,
      render: (i) => <span className="text-mono">{i.dispatch?.gr_no || "—"}</span> },
    { key: "taxable", header: "Taxable", align: "right", accessor: (i) => dsp(i).taxable || 0,
      render: (i) => <span className="text-mono">{fmtCur(dsp(i).taxable, dsp(i).currency_code)}</span> },
    { key: "gst", header: "GST", align: "right", accessor: (i) => dsp(i).gst || 0,
      render: (i) => dsp(i).gst == null
        ? <span className="muted" title="Export invoice — no GST">—</span>
        : <span className="text-mono">{fmtCur(dsp(i).gst, dsp(i).currency_code)}</span> },
    { key: "gtotal", header: "G Total", align: "right", accessor: (i) => dsp(i).gtotal || 0,
      render: (i) => <span className="text-mono" style={{ fontWeight: 700 }}>{fmtCur(dsp(i).gtotal, dsp(i).currency_code)}</span> },
  ];

  const toolbar = (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      <span className="muted" style={{ fontSize: 11, fontWeight: 600 }}>PLANT</span>
      <select className="jde-input" value={plant} onChange={(e) => setParam("plant", e.target.value)} data-testid="filter-plant">
        <option value="">All plants</option>
        {plants.map((p) => <option key={p} value={p}>{p}</option>)}
      </select>
      <span className="muted" style={{ fontSize: 11, fontWeight: 600 }}>CUSTOMER</span>
      <select className="jde-input" value={customer} onChange={(e) => setParam("customer", e.target.value)} data-testid="filter-customer">
        <option value="">All customers</option>
        {customers.map((c, i) => <option key={`${c.ms_pcode || c.name}-${i}`} value={c.ms_pcode || ""}>{`${c.name}${c.ms_pcode ? ` (${c.ms_pcode})` : ""}`}</option>)}
      </select>
    </div>
  );

  return (
    <Layout breadcrumb={["Home", "Dispatch"]}>
      <div className="jde-page">
        <div className="jde-panel">
          <div className="jde-panel-header">
            <div className="jde-panel-title">DISPATCHES ({invs.length}{loading ? "…" : ""})</div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="jde-btn jde-btn-sm" onClick={downloadPoTransferReconcile} disabled={dlpt} data-testid="disp-po-transfer-reconcile-report" title="Verify the Excel→MS SQL PO transfer: compares each OPEN PO's items, ordered qty and open qty (aggregated across delivery schedules) in Excel/Mongo vs MS SQL New_PO/New_POi, plus a tab of POs not transferred">
                <DownloadSimple size={13} weight="bold" /> {dlpt ? "Preparing…" : "PO Transfer Reconcile"}
              </button>
              <button className="jde-btn jde-btn-sm" onClick={downloadOpenPoReconcile} disabled={dlo} data-testid="disp-openpo-reconcile-report" title="Download an Excel audit of OPEN POs only: compares each PO's dispatch legs (invoice #, part, qty) from Excel against MS SQL (invoicei/Gsti), with a summary and invoice-level detail tab">
                <DownloadSimple size={13} weight="bold" /> {dlo ? "Preparing…" : "Open-PO Reconcile"}
              </button>
              <button className="jde-btn jde-btn-sm" onClick={downloadReconcile} disabled={dlr} data-testid="disp-reconcile-report" title="Download an Excel report of Excel↔MS SQL discrepancies (PO / item / qty mismatches, unmatched, awaiting-invoice)">
                <DownloadSimple size={13} weight="bold" /> {dlr ? "Preparing…" : "Excel↔MS SQL Reconcile"}
              </button>
              <button className="jde-btn jde-btn-sm" onClick={downloadDateFixReport} disabled={dl} data-testid="disp-datefix-report" title="Download an Excel report of invoices whose Sale Date differs from the ERP — to fix source sheets before re-importing">
                <DownloadSimple size={13} weight="bold" /> {dl ? "Preparing…" : "Date-Fix Report (Excel)"}
              </button>
            </div>
          </div>
          <div className="jde-panel-body" style={{ padding: 0 }}>
            <SortableGrid pageSize={100}
              columns={columns}
              rows={invs}
              getRowKey={(i) => i.id || i.ms_sale_no}
              renderExpanded={(i) => <InvoiceLinesDetail inv={i} nav={nav} />}
              defaultSort={{ key: "sale_date", dir: "desc" }}
              searchPlaceholder="Search sale #, invoice #, PO #, vehicle, customer…"
              testid="dispatch-table"
              toolbarRight={toolbar}
              emptyMessage="No dispatches."
            />
          </div>
        </div>
      </div>
    </Layout>
  );
}

function InvoiceLinesDetail({ inv, nav }) {
  const lines = inv.lines || [];
  const cur = inv.currency_code || inv.currency || "USD";
  const th = { textAlign: "left", padding: "6px 10px", fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, color: "#64748B", borderBottom: "1px solid var(--jde-border)", whiteSpace: "nowrap" };
  const td = { padding: "6px 10px", fontSize: 12.5, borderBottom: "1px solid #EEF1F4" };
  return (
    <div style={{ padding: "10px 16px 14px" }} data-testid={`disp-lines-${inv.ms_sale_no}`}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5, color: "#475569" }}>
          Line items · Inv #{inv.ms_inv_no || inv.ms_sale_no} <span className="muted" style={{ fontWeight: 500 }}>({lines.length})</span>
        </span>
        <span className="muted" style={{ fontSize: 11 }}>{inv.customer_name} · {fmtDate(inv.sale_date)} · PO {inv.ms_pono || "—"}</span>
        <span style={{ flex: 1 }} />
        <button className="jde-btn jde-btn-sm" onClick={(e) => { e.stopPropagation(); nav(`/dispatch/${inv.ms_sale_no}`); }} data-testid={`disp-view-full-${inv.ms_sale_no}`}>
          View full dispatch →
        </button>
      </div>
      <div style={{ overflowX: "auto", border: "1px solid var(--jde-border)", borderRadius: 6, background: "white" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>
            {[["#", "left"], ["Item", "left"], ["Description", "left"], ["PO #", "left"], ["Qty", "right"], ["Unit", "left"], ["Rate", "right"], ["Taxable", "right"], ["GST", "right"], ["Total", "right"]].map(([h, a]) => (
              <th key={h} style={{ ...th, textAlign: a }}>{h}</th>))}
          </tr></thead>
          <tbody>
            {lines.map((l, idx) => {
              const gst = (Number(l.cgst_amt) || 0) + (Number(l.sgst_amt) || 0) + (Number(l.igst_amt) || 0);
              return (
                <tr key={`${l.micode}-${idx}`}>
                  <td style={td} className="text-mono">{l.sno || idx + 1}</td>
                  <td style={{ ...td, fontWeight: 700 }} className="text-mono">{l.micode || "—"}</td>
                  <td style={td}>{l.name || "—"}</td>
                  <td style={td} className="text-mono">{l.pono || "—"}</td>
                  <td style={{ ...td, textAlign: "right" }} className="text-mono">{fmtNum(l.qty)}</td>
                  <td style={td}>{l.unit || "—"}</td>
                  <td style={{ ...td, textAlign: "right" }} className="text-mono">{l.rate}</td>
                  <td style={{ ...td, textAlign: "right" }} className="text-mono">{fmtCur(l.taxable_amt, cur)}</td>
                  <td style={{ ...td, textAlign: "right" }} className="text-mono">{gst > 0 ? fmtCur(gst, cur) : <span className="muted">—</span>}</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 700 }} className="text-mono">{fmtCur(l.tot_amt, cur)}</td>
                </tr>);
            })}
            {lines.length === 0 && <tr><td colSpan={10} className="muted" style={{ padding: 14, textAlign: "center" }}>No line items on this invoice.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
